import { useState, SyntheticEvent, useEffect } from 'react';
import { useRouter } from 'next/router';

import { get, post } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import { apiPath, loginPath, webPath } from 'presentations/routes/path';
import { IntegrationMergedTeamShopAccountItem } from 'presentations/api/schema/team_shop_account';
import { setEvent } from 'presentations/views/helpers/analytics';
import { isiOSApp as isiOSAppFunc } from 'presentations/views/helpers/electron';

interface Props {
  viewer: ViewerItem | null;
}

export function useServicePaymentPage(props: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isiOSApp = isiOSAppFunc(userAgent);
  const [
    shopAccount,
    setShopAccount,
  ] = useState<IntegrationMergedTeamShopAccountItem | null>(null);
  const router = useRouter();

  useEffect(() => {
    (async () => {
      if (props.viewer) {
        setIsLoading(true);
        const findResult = await get(
          apiPath(`/v1/teams/${props.viewer.team?.uid}/shop_account`),
        );
        if (!findResult.isSuccess) {
          setIsLoading(false);
          setEvent('show payment setting page');
          return;
        }
        setShopAccount(findResult.data);
        setIsLoading(false);
      }
    })();
  }, []);

  const onConnect = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (isiOSApp) {
      if (
        typeof window.webkit?.messageHandlers?.requestToOpenBrowser
          ?.postMessage === 'function'
      ) {
        window.webkit.messageHandlers.requestToOpenBrowser.postMessage(
          webPath(
            loginPath({ to: router.asPath, redirectIfLoggedIn: true }),
            router.locale,
          ),
        );
      }
      return;
    }

    if (!props.viewer) return;
    setIsLoading(true);
    if (!shopAccount) {
      const result = await post(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/shop_account/create`),
      );
      if (!result.isSuccess && result.status !== 409) {
        setIsLoading(false);
        return;
      }
    }

    const result = await post(
      apiPath(`/v1/teams/${props.viewer.team?.uid}/shop_account/create_link`),
    );
    if (!result.isSuccess) {
      return;
    }
    router.push(result.data);
    setEvent('click stripe account link');
  };

  return {
    state: {
      shopAccount,
      isLoading,
    },
    actions: {
      onConnect,
    },
  };
}
